import { Injectable } from '@angular/core';
import { ICardInfo, IDisplayFilters } from '../interfaces/stanalone-components.data';
import { ICamparisonInfo, ReusableFunctions } from '../../../../shared/classes/reusable-functions';
import { NoDataActionType } from '../../../../shared/interfaces/no-data';
import { ProductionCharts } from '../../../../shared/classes/production-chart-options';
import { Production } from '../../../../production/interfaces/production';
import { IChartTitleData, LargeCardDataFromDb } from '../../../../shared/interfaces/card-data';
import * as Highcharts from 'highcharts';
import { DisplayOptions } from '../../../../shared/interfaces/display-options';
import { IPipelineParameters } from '../../../../pipeline/interfaces/pipeline-data';
import { PipelineService } from '../../../../pipeline/services/pipeline.service';
import { LimeGearCharts } from '../../../../shared/classes/chart-data';
import { PipelineChartOptions } from '../../../../shared/classes/pipeline-chart-options';
import { UserAccessService } from '../../../../shared/services/user-access.service';
import { iFilterParams } from '../../../interfaces/my-limegear.interface';
import { NavigationHelpService } from 'app/modules/shared/services/navigation-help.service';

interface ILabelCollection {
    index: number;
    description: string;
}

@Injectable({
    providedIn: 'root',
})
export class LargeCardService {
    private serverUrl = 'https://' + window.location.host; // TODO Get url and version number from appsettings.
    private version = '0.5';
    private componentName = 'pipeline';
    private _url = (this.serverUrl + '/api/v{version}/{componentName}')
        .replace('{version}', this.version)
        .replace('{componentName}', this.componentName);

    constructor(
        private reusableFunctions: ReusableFunctions,
        private _pipelineServices: PipelineService,
        private _userAccessService: UserAccessService,
        private navigationHelpService: NavigationHelpService
    ) {}

    initializeLargeCard(
        largeCardInfo: ICardInfo,
        action: NoDataActionType,
        cardDrilldownUrl: string
    ) {
        let largeCardInteriorOptions = {
            action: action,
            componentTitle: largeCardInfo.name,
            searchTerm: '',
            hasData: false,
            isDataReady: false,
            showTitle: true,
            accessToUrl: cardDrilldownUrl + '/' + largeCardInfo.urlSegment,
            uniqueId: largeCardInfo.urlSegment,
            showCardControllers: true,
        };
        largeCardInfo.largeCardInteriorOptions = Object.assign({}, largeCardInteriorOptions);
    }

    getDisplayOptionData(displayOptions: DisplayOptions[], hFilterJSON: string) {
        let options: IDisplayFilters = {
            compareInfo_selectedOptions: '',
            compareInfo_selectedOptionsFriendly: '',
            dateRange: '',
            channel: '',
            loanType: '',
            loanPurpose: '',
            hFilterJSON: '[]',
            milestone: '',
        };

        if (displayOptions && displayOptions.length > 0) {
            let compareInfo = displayOptions.find((x) => x.optionKey === 'compareTo');
            options.compareInfo_selectedOptions = compareInfo?.selectedOptions ?? '';
            options.compareInfo_selectedOptionsFriendly =
                compareInfo?.selectedOptionsFriendly ?? '';
            options.dateRange =
                displayOptions.find((x) => x.optionKey === 'dateRange_y_q_m')?.selectedOptions ??
                '';
            options.channel =
                displayOptions.find((x) => x.optionKey === 'channel')?.selectedOptions ?? 'All';
            options.loanType =
                displayOptions.find((x) => x.optionKey === 'loanCategory')?.selectedOptions ??
                'All';
            options.loanPurpose =
                displayOptions.find((x) => x.optionKey === 'loanPurpose')?.selectedOptions ?? 'All';
            options.milestone =
                displayOptions.find((x) => x.optionKey === 'activeMilestones')?.selectedOptions ??
                'All';
        }

        if (hFilterJSON) {
            options.hFilterJSON = hFilterJSON;
        }
        return options;
    }

    setChartData(
        stackData: Production[],
        lineData: Production[],
        property: string,
        isStacking: boolean,
        groupByClass: string,
        tootipTotalName: string,
        colors: object,
        dateRange: string
    ) {
        let dataSet = stackData.map((obj) => ({ ...obj }));

        let lengendItemsIndex = {
            Purchase: 0,
            Refinance: 1,
            Construction: 2,
            Modification: 3,
            Other: 4,
            Conv: 0,
            Govt: 1,
            Jumbo: 2,
            Canceled: 0,
            Denied: 1,
            Withdrawn: 2,
            Inactive: 3,
        };

        let stackColumnsData: ProductionCharts.ChartdataSeries[] = [];
        let xAxisLabelList: ILabelCollection[] = [];
        if (isStacking) {
            let groupingNames = this.reusableFunctions.getUniqueItemList(dataSet, groupByClass);
            groupingNames.forEach((ItemClass) => {
                let classData = dataSet.filter((item) => item[groupByClass] === ItemClass);
                let dataInf = this.setPointData(classData, property, tootipTotalName, dateRange);
                stackColumnsData.push({
                    name: ItemClass,
                    data: dataInf.dataSet,
                    legendIndex: lengendItemsIndex[ItemClass],
                    index: lengendItemsIndex[ItemClass],
                    color: colors[ItemClass],
                    type: 'column',
                });

                dataInf?.xLabelCollection?.forEach((xLabelInf) => {
                    let indx = xAxisLabelList.findIndex(
                        (labelInf) => labelInf.index === xLabelInf.index
                    );
                    if (indx < 0) {
                        xAxisLabelList.push({
                            index: xLabelInf.index,
                            description: xLabelInf.description,
                        });
                    }
                });
            });
        } else {
            let pointDataInf = this.setPointData(dataSet, property, '', dateRange);
            stackColumnsData.push({
                name: 'This Period',
                data: pointDataInf.dataSet,
                color: colors[groupByClass],
                type: 'column',
            });
            xAxisLabelList = pointDataInf?.xLabelCollection;
        }
        let lineChartData = this.setLineChartData(lineData, property, xAxisLabelList, dateRange);
        return { stackColumnsData: stackColumnsData, lineData: lineChartData };
    }

    setPointData(
        classData: Production[],
        property: string,
        tootipTotalName: string,
        dateRange: string
    ) {
        let purposeItems: ProductionCharts.ChartDataPoint[] = [];
        let xLabelCollection: ILabelCollection[] = [];
        classData.forEach((item) => {
            let name = this.reusableFunctions.setXValues(item, dateRange);
            xLabelCollection.push({
                index: item.period,
                description: item.datePeriodDescription,
            });

            purposeItems.push({
                y: item[property],
                name: name,
                properties: {
                    datePeriodDescription: item.datePeriodDescription,
                    period: item.period,
                    units: item.nbr,
                    tootipTotalName: tootipTotalName,
                },
            });
        });
        return { dataSet: purposeItems, xLabelCollection: xLabelCollection };
    }

    setLineChartData(
        data: Production[],
        property: string,
        xlabelsList: ILabelCollection[],
        dateRange: string
    ) {
        let seriesData: ProductionCharts.ChartDataPoint[] = [];
        if (data.length > 0) {
            let dataSet = data.map((obj) => ({ ...obj }));
            let groupingNames = this.reusableFunctions.getUniqueItemList(dataSet, 'period');
            groupingNames.forEach((period, index) => {
                let groupsData = dataSet.filter((item) => item.period === period);
                if (groupsData.length > 0 && xlabelsList.length > index) {
                    let xLabel: number | string = groupsData[0].period;
                    if (this.reusableFunctions.getChartXLabel(dateRange) === 'Month') {
                        xLabel = xlabelsList[index]?.description?.slice(0, 3);
                    }

                    seriesData.push({
                        y: this.reusableFunctions.calculateSumByProperty(groupsData, property),
                        name: xLabel.toString(),
                        properties: {
                            datePeriodDescription: groupsData[0].datePeriodDescription,
                            period: groupsData[0].period,
                            units: this.reusableFunctions.calculateSumByProperty(groupsData, 'nbr'),
                        },
                    });
                }
            });
        }
        return seriesData;
    }

    getChartTitleData(
        title: string,
        compareValue: number,
        comparisonPercent: number,
        isComparable: boolean,
        subValue: string,
        mainValue: number,
        secondaryValue: number,
        isSubValueVisible: boolean,
        ismainValueUnit: boolean,
        isInverseCompareValue: boolean
    ) {
        let isMainValueUnitVisible = true;
        let mainValueToShow: string = '';
        let secondaryValueToShow: string = '';
        let comparison = this.reusableFunctions.getCaret(
            compareValue,
            isInverseCompareValue,
            comparisonPercent,
            isComparable
        );
        let comparisonValue = comparison.comparison;

        if (ismainValueUnit) {
            mainValueToShow = this.reusableFunctions.formatUnits(mainValue);
            comparisonValue = comparisonValue.replace('$', '');
            secondaryValueToShow = this.reusableFunctions.formatMoney(secondaryValue);
        } else {
            mainValueToShow = this.reusableFunctions.formatMoney(mainValue);
            comparisonValue;
            secondaryValueToShow = this.reusableFunctions.formatUnits(secondaryValue);
        }

        if (subValue === null) {
            isSubValueVisible = false;
        }

        let chartTitleData: IChartTitleData;
        if (ismainValueUnit) {
            chartTitleData = {
                showChartCustomTitle: true,
                chartTile: title,
                chartLabelColor: comparison.color,
                chartComparisonValue: comparisonValue,
                chartMainValue: secondaryValueToShow,
                chartSecondaryValue: mainValueToShow,
                isMainValueUnitVisible: isMainValueUnitVisible,
                chartSubItemValue: subValue,
                isChartSubValueVisible: isSubValueVisible,
                svgIconString: comparison.svgData,
            };
        } else {
            chartTitleData = {
                showChartCustomTitle: true,
                chartTile: title,
                chartLabelColor: comparison.color,
                chartComparisonValue: comparisonValue,
                chartMainValue: mainValueToShow,
                chartSecondaryValue: secondaryValueToShow,
                isMainValueUnitVisible: isMainValueUnitVisible,
                chartSubItemValue: subValue,
                isChartSubValueVisible: isSubValueVisible,
                svgIconString: comparison.svgData,
            };
        }
        return chartTitleData;
    }

    updateChartData(
        cardInfo: ICardInfo,
        chartOptions: Highcharts.Options,
        hasData: boolean,
        isDataReady: boolean,
        action: NoDataActionType,
        comparison: ICamparisonInfo,
        mainValue: number,
        secondaryValue: number,
        showUnits,
        isComparisonInverse: boolean,
        loanPurpose: string,
        loanType: string,
        compareTo: string
    ) {
        let largeCardInteriorOptions = Object.assign({}, cardInfo.largeCardInteriorOptions);
        if (hasData) {
            largeCardInteriorOptions.chart = new Highcharts.Chart(
                largeCardInteriorOptions.uniqueId,
                chartOptions
            );
        }
        let queryParams = {
            purpose: loanPurpose,
            loanType: loanType,
            compareTo: compareTo,
            periodGroup: 'T',
        };

        if (cardInfo.urlSegment.search('fall-out') > -1) {
            queryParams['falloutType'] = 'All';
        }

        let chartTitleData;

        if (comparison) {
            chartTitleData = this.getChartTitleData(
                cardInfo.name,
                comparison.comparisonValue,
                comparison.percentage,
                comparison.isComparable,
                null,
                mainValue,
                secondaryValue,
                false,
                showUnits,
                isComparisonInverse
            );
        }

        largeCardInteriorOptions.chartTitleData = chartTitleData;
        largeCardInteriorOptions.queryParams = Object.assign({}, queryParams);
        largeCardInteriorOptions.hasData = hasData;
        largeCardInteriorOptions.isDataReady = isDataReady;
        largeCardInteriorOptions.searchTerm = '';
        largeCardInteriorOptions.action = action;
        largeCardInteriorOptions.showCardControlArea = true;
        return Object.assign({}, largeCardInteriorOptions);
    }

    setAllChartSeriesOptions(
        cardInfo: ICardInfo,
        lineData: ProductionCharts.ChartDataPoint[],
        stackColumnData: ProductionCharts.ChartdataSeries[],
        compareTo: string,
        cardDrilldownUrl: string,
        compareToFriendly: string,
        loanPurpose: string,
        loanType: string,
        limeGearCharts: ProductionCharts.ProductionCharts,
        queryParams = undefined,
        urlSegment: string = undefined
    ) {
        let seresData: ProductionCharts.ChartdataSeries[] = [];
        if (lineData) {
            let lineSeries = limeGearCharts.setLineChartSeriesOptions(
                lineData,
                'spline',
                '#A6266E',
                'circle',
                true,
                compareTo,
                cardDrilldownUrl,
                urlSegment ?? this.reusableFunctions.formatTitle(cardInfo.name),
                compareToFriendly,
                loanPurpose,
                loanType,
                queryParams
            );
            seresData = stackColumnData.concat([lineSeries as ProductionCharts.ChartdataSeries]);
        }
        return seresData;
    }

    getPipelineLargecardData(cardInfo: ICardInfo, filterParams: iFilterParams, mainUrl: string) {
        let filters = this.getDisplayOptionData(
            filterParams.displayOptions,
            filterParams.hierarchyfilters
        );

        let parameters: IPipelineParameters = {
            fromDatePeriod: filters.dateRange,
            channel: filters.channel,
            loanType: filters.loanType,
            loanPurpose: filters.loanPurpose,
            hFilterJSON: filters.hFilterJSON,
            priorCompareTo: filters.compareInfo_selectedOptions,
            milestone: filters.milestone,
        };

        let params = Object.assign({}, parameters);
        params.milestoneKey = cardInfo.milestoneKey.toString();
        params.cardId = cardInfo.urlSegment;
        this._pipelineServices.getLargeCardData(params).subscribe(
            (data) => {
                this.setPipelineLargeCardData(data, cardInfo, mainUrl, filters);
            },
            (err) => {
                this.setPipelineLargeCardData(
                    [],
                    cardInfo,
                    mainUrl,
                    filters,
                    NoDataActionType.error
                );
                throw new Error(err);
            }
        );
    }

    setPipelineLargeCardData(
        dataList: LargeCardDataFromDb[],
        cardInf: ICardInfo,
        mainUrl: string,
        filters: IDisplayFilters,
        noDataType: NoDataActionType = cardInf.largeCardInteriorOptions.action
    ) {
        let largeCardInteriorOptions = cardInf.largeCardInteriorOptions;
        let largeCardData = {
            title: cardInf.name,
            totalVolume: 0,
            comparisonVolume: 0,
            comparisonPercent: 0,
            estimatelVolume: null,
            appsChartData: {
                current: null,
                line: null,
                prediction: null,
            },
            categoryGroup: [],
        };
        let volumeProperty = 'amount';
        let unitProperty = 'nbr';
        let sumVolumeThisPeriod = 0;
        let sumUnitsThisPeriod = 0;
        let sumLastPeriod = 0;
        let selectedPeriod = '';

        let dataThisPeriod = dataList.filter((obj) => obj.periodGroup === 'This Period');
        let dataLastPeriod = dataList.filter((obj) => obj.periodGroup === 'Compare Period');

        if (cardInf.urlSegment === 'total-pipeline' || cardInf.urlSegment === 'locked-pipeline') {
            volumeProperty = 'aveAmount';
            unitProperty = 'aveNbr';
            if (dataList.length > 0) {
                let lastLineThisPeriod = dataThisPeriod[dataThisPeriod.length - 1];
                let lastLineLastPeriod = dataLastPeriod[dataLastPeriod.length - 1];
                sumVolumeThisPeriod = lastLineThisPeriod.aveAmount;
                sumUnitsThisPeriod = lastLineThisPeriod.aveNbr;
                sumLastPeriod = lastLineLastPeriod.aveAmount;
                selectedPeriod = lastLineThisPeriod.period
                    ? lastLineThisPeriod.period.toString()
                    : '';
            }
        } else {
            sumVolumeThisPeriod = this.reusableFunctions.calculateSumByProperty(
                dataThisPeriod,
                volumeProperty
            );

            sumUnitsThisPeriod = this.reusableFunctions.calculateSumByProperty(
                dataThisPeriod,
                unitProperty
            );

            sumLastPeriod = this.reusableFunctions.calculateSumByProperty(
                dataLastPeriod,
                volumeProperty
            );
        }

        let comparison = this.reusableFunctions.calculateComparisonPercentage(
            sumVolumeThisPeriod,
            sumLastPeriod
        );
        largeCardData.title = cardInf.name;
        let categoryInf = this.reusableFunctions.getCategories(
            dataThisPeriod,
            ['year', 'period'],
            filters.dateRange
        );
        let queryParams = {
            compareTo: filters.compareInfo_selectedOptions,
            selectedPeriod: selectedPeriod,
            periodGroup: 'T',
            titleSuffix: '',
        };

        let chartTitleData = this.getChartTitleData(
            cardInf.name,
            comparison.comparisonValue,
            comparison.percentage,
            comparison.isComparable,
            null,
            sumVolumeThisPeriod,
            sumUnitsThisPeriod,
            false,
            false,
            false
        );

        largeCardData.categoryGroup = categoryInf[0];
        largeCardData.totalVolume = Math.round(sumVolumeThisPeriod);

        largeCardData.comparisonVolume = Math.round(comparison[0]);
        largeCardData.comparisonPercent = comparison[1];
        largeCardData.appsChartData = this.setDataForCharts(
            dataThisPeriod,
            dataLastPeriod,
            volumeProperty,
            unitProperty,
            categoryInf[1],
            filters.dateRange
        );
        largeCardData.estimatelVolume = 0.0; // TODO SET TO NULL ONCE THE VALUE IS AVAILABLE
        largeCardInteriorOptions.chart = new Highcharts.Chart(
            largeCardInteriorOptions.uniqueId,
            this.drawChart('current', 'none', largeCardData, cardInf.urlSegment, filters, mainUrl)
        );

        largeCardInteriorOptions.chartTitleData = chartTitleData;
        largeCardInteriorOptions.queryParams = Object.assign({}, queryParams);
        largeCardInteriorOptions.hasData = dataList.length > 0;
        largeCardInteriorOptions.isDataReady = true;
        largeCardInteriorOptions.showCardControlArea = true;
        largeCardInteriorOptions.action = noDataType;
        cardInf.largeCardInteriorOptions = Object.assign({}, largeCardInteriorOptions);
    }

    setDataForCharts(
        dataThisPeriod: LargeCardDataFromDb[],
        dataLastPeriod: LargeCardDataFromDb[],
        volumeProperty: string,
        unitProperty: string,
        categories: Array<any>,
        fromDatePeriod: string
    ): LimeGearCharts.ChartDataSeparation {
        const colorNormal = '#41d6c3';
        const colorPredict = '#FFE699';
        const current: Array<LimeGearCharts.ChartDataPoint> = [];
        const line: Array<LimeGearCharts.ChartDataPoint> = [];
        const prediction: Array<LimeGearCharts.ChartDataPoint> = [];
        let dataPointCurrent: LimeGearCharts.ChartDataPoint;
        let dataPointLine: LimeGearCharts.ChartDataPoint;
        let dataPointPredict: LimeGearCharts.ChartDataPoint;

        let xLabels: Array<string | number> = [];

        dataThisPeriod.forEach((dataPointThis, index) => {
            let xLabel: number | string = dataPointThis.period;
            if (this.reusableFunctions.getChartXLabel(fromDatePeriod) === 'Month') {
                xLabel = dataPointThis.datePeriodDescription.slice(0, 3);
            }
            xLabels.push(xLabel);
            dataPointCurrent = {
                y: dataPointThis[volumeProperty] === null ? 0 : dataPointThis[volumeProperty],
                color: colorNormal,
                properties: {
                    datePeriodDescription: dataPointThis.datePeriodDescription,
                    period: dataPointThis.period,
                    units: dataPointThis[unitProperty],
                },
                name: xLabel.toString(),
            };

            if (dataPointThis.period === 0 || dataPointThis.period == null) {
                dataPointPredict = {
                    y: dataPointThis[volumeProperty], // TOBE CHANGE TO PREDICTION VALUES
                    color: colorPredict,
                    properties: {
                        datePeriodDescription: dataPointThis.datePeriodDescription,
                        period: dataPointThis.period,
                        units: dataPointThis[unitProperty],
                    },
                    name: xLabel.toString(),
                };
            } else {
                dataPointPredict = {
                    y: dataPointThis[volumeProperty],
                    color: colorNormal,
                    properties: {
                        datePeriodDescription: dataPointThis.datePeriodDescription,
                        period: dataPointThis.period,
                        units: dataPointThis[unitProperty],
                    },
                    name: xLabel.toString(),
                };
            }
            current.push(dataPointCurrent);
            prediction.push(dataPointPredict);
        });

        dataLastPeriod.forEach((dataPointLast, index) => {
            if (categories.length > index) {
                let xLabel: number | string = dataPointLast.period;
                if (this.reusableFunctions.getChartXLabel(fromDatePeriod) === 'Month') {
                    xLabel = xLabels[index];
                }

                dataPointLine = {
                    y: dataPointLast[volumeProperty],
                    properties: {
                        datePeriodDescription: dataPointLast.datePeriodDescription,
                        period: dataPointLast.period,
                        units: dataPointLast[unitProperty],
                    },
                    name: xLabel.toString(),
                };

                line.push(dataPointLine);
            }
        });

        return {
            current: current,
            line: line,
            prediction: prediction,
        };
    }

    drawChart(
        switchedDataSet,
        showEstimate,
        largeCardData,
        urlSegment: string,
        flters: IDisplayFilters,
        cardDrilldownUrl: string
    ) {
        const loadOptions = new PipelineChartOptions(
            this.reusableFunctions,
            this.navigationHelpService
        );
        const xTitle = this.reusableFunctions.getChartXLabel(flters.dateRange);

        let chartOptions = loadOptions.loadChartOptions(
            largeCardData.appsChartData[switchedDataSet],
            largeCardData.appsChartData.line,
            largeCardData.title,
            flters.compareInfo_selectedOptionsFriendly,
            this.checkAccess(cardDrilldownUrl + '/' + urlSegment),
            xTitle,
            cardDrilldownUrl + '/' + urlSegment,
            flters.compareInfo_selectedOptions,
            largeCardData.categoryGroup
        );
        return chartOptions;
    }

    checkAccess(url: string) {
        return this._userAccessService.hasAccessToPage(url);
    }
}
